import React, { useEffect, useMemo, useState } from "react";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Stack } from "@mui/material";
import { formatISO } from "date-fns";
import { useLocation } from "react-router-dom";

import useGetTaskTemplate from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTaskTemplate";
import {
  TaskDetails,
  TemplateInfo,
} from "@fartherfinance/frontend/api/TaskWorkflows/Types";

// Encapsulated task creation data
interface TaskCreationData {
  taskType?: string;
  taskTypeId?: number;
  client?: string;
  taskTypeTemplate?: string;
}

// Tagged union for location state
interface CreateTaskLocationState {
  type: "createTask";
  data: TaskCreationData;
}

interface SubtaskLocationState {
  type: "subtask";
  taskDetails: TaskDetails;
}

// Union type for all possible location states
type LocationState = CreateTaskLocationState | SubtaskLocationState | undefined;

import { ATTACHMENTS_LIMIT_COUNT } from "../../Tasks/common";
import TaskWorkflows from "../../TaskWorkflows";
import TaskActionButtons from "../components/TaskActionButtons";
import { SIMPLE_TEMPLATE_ID } from "../constants";
import { FormEngine } from "../FormEngine";
import {
  FormSubmitProvider,
  useFormSubmit,
} from "../FormEngine/context/FormSubmitContext";
import { useTasksNavigation } from "../hooks";
import TaskMessageModal from "../TaskMessageModal/TaskMessageModal";
import Drawer from "@src/multiCustodian/components/Drawer/Drawer";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useFilesManagement from "@src/multiCustodian/hooks/useFilesManagement";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import { MenuOption } from "@src/sharedComponents/Menu/MenuOption";
import Text from "@src/sharedComponents/Typography/Text";
import Upload from "@src/sharedComponents/Upload/Upload";

import AttachmentsSection from "./AttachmentsSection";

import styles from "../../Tasks/CreateTask/CreateTask.module.css";

const mapParentTaskToInitialValues = (
  parentTask: TaskDetails
): Record<string, unknown> => {
  // Preprocess parent task to convert Date objects to strings before Zod validation
  const preprocessedParentTask = {
    ...parentTask,
    fields: {
      ...parentTask.fields,
      // Convert Date to string format that Zod expects
      dueDate:
        parentTask.fields?.dueDate instanceof Date
          ? formatISO(parentTask.fields.dueDate, { representation: "date" }) // Converts to "YYYY-MM-DD"
          : parentTask.fields?.dueDate, // Keep null/string as-is
    },
  };

  const validatedParentTask = TaskDetails.safeParse(preprocessedParentTask);

  if (!validatedParentTask.success) {
    console.warn(
      "[CreateTaskWorkflow] Invalid parent task structure:",
      validatedParentTask.error
    );
    return {
      taskStatus: { id: "NOT_STARTED" },
    };
  }

  const validParent = validatedParentTask.data;

  if (!validParent.fields || typeof validParent.fields !== "object") {
    console.warn(
      "[CreateTaskWorkflow] Parent task has no fields or invalid fields structure"
    );
    return {
      taskStatus: { id: "NOT_STARTED" },
    };
  }

  const initialValues = {
    ...validParent.fields,
    title: undefined, // Clear title so user can set new subtask title
    taskStatus: { id: "NOT_STARTED" }, // Always start subtasks as NOT_STARTED
  };

  return initialValues;
};

const mapCreateTaskDataToInitialValues = (
  taskCreationData: TaskCreationData
): Record<string, unknown> => {
  const initialValues: Record<string, unknown> = {};

  if (taskCreationData.client) {
    initialValues.client = taskCreationData.client;
  }

  if (taskCreationData.taskTypeId !== undefined) {
    initialValues.taskType = taskCreationData.taskTypeId.toString();
  } else if (taskCreationData.taskType) {
    initialValues.taskType = taskCreationData.taskType;
  }

  // Set default task status for new tasks
  initialValues.taskStatus = { id: "NOT_STARTED" };

  return initialValues;
};

interface CreateTaskWorkflowProps {
  isSubtaskCreation?: boolean;
}

const CreateTaskWorkflowWithModal: React.FC<CreateTaskWorkflowProps> = ({
  isSubtaskCreation,
}) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [shouldCloseDrawer, setShouldCloseDrawer] = useState(false);

  const handleConfirmClose = (): void => {
    setIsConfirmModalOpen(false);
    setShouldCloseDrawer(true);
  };

  const handleCancelClose = (): void => {
    setIsConfirmModalOpen(false);
  };

  const handleShowConfirmModal = (): void => {
    setIsConfirmModalOpen(true);
  };

  const handleDrawerClosed = (): void => {
    setShouldCloseDrawer(false);
  };

  return (
    <CreateTaskWorkflowInner
      isSubtaskCreation={isSubtaskCreation}
      onShowConfirmModal={handleShowConfirmModal}
      shouldCloseDrawer={shouldCloseDrawer}
      onDrawerClosed={handleDrawerClosed}
      isConfirmModalOpen={isConfirmModalOpen}
      onConfirmModalClose={handleCancelClose}
      onConfirmModalConfirm={handleConfirmClose}
    />
  );
};

interface CreateTaskWorkflowInnerProps extends CreateTaskWorkflowProps {
  onShowConfirmModal: () => void;
  shouldCloseDrawer: boolean;
  onDrawerClosed: () => void;
  isConfirmModalOpen: boolean;
  onConfirmModalClose: () => void;
  onConfirmModalConfirm: () => void;
}

const CreateTaskWorkflowInner: React.FC<CreateTaskWorkflowInnerProps> = ({
  isSubtaskCreation,
  onShowConfirmModal,
  shouldCloseDrawer,
  onDrawerClosed,
  isConfirmModalOpen,
  onConfirmModalClose,
  onConfirmModalConfirm,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const location = useLocation<LocationState>();

  const { files, setFiles, removeFile } = useFilesManagement({
    countLimit: ATTACHMENTS_LIMIT_COUNT,
  });
  const { handleNavigateToTasksList } = useTasksNavigation();
  const statusNotification = useStatusNotification();
  const auth = useAdvisorRequestAuth();
  const { isDirty } = useFormSubmit();

  // Get location state data
  const locationState = location.state;

  // Extract data based on the tagged union type
  const createTaskData =
    locationState?.type === "createTask" ? locationState : undefined;
  const subtaskLocationData =
    locationState?.type === "subtask" ? locationState : undefined;
  const parentTaskDetails = subtaskLocationData?.taskDetails;

  // Get template ID - use from createTaskData if available, otherwise fallback to SIMPLE_TEMPLATE_ID
  const templateId =
    createTaskData?.data.taskTypeTemplate || SIMPLE_TEMPLATE_ID;

  const {
    data: templateData,
    isLoading,
    hasError,
  } = useGetTaskTemplate(templateId, auth);

  const template = useMemo(() => {
    return TemplateInfo.safeParse(templateData);
  }, [templateData]);

  // Compute initial values based on the type of workflow
  const initialValues = useMemo(() => {
    if (isSubtaskCreation && parentTaskDetails) {
      return mapParentTaskToInitialValues(parentTaskDetails);
    }
    if (!isSubtaskCreation && createTaskData) {
      return mapCreateTaskDataToInitialValues(createTaskData.data);
    }
    return undefined;
  }, [isSubtaskCreation, parentTaskDetails, createTaskData]);

  const handleCloseDrawer = (): void => {
    if (isDirty) {
      onShowConfirmModal();
    } else {
      setIsDrawerOpen(false);
    }
  };

  useEffect(() => {
    if (shouldCloseDrawer) {
      setIsDrawerOpen(false);
      onDrawerClosed();
    }
  }, [shouldCloseDrawer, onDrawerClosed]);

  const handlePersistFiles = (files: FileList | null): void => {
    if (files && files.length) {
      setFiles(files);
    }
  };

  const handleCreateMenuOptions = (fileName: string): MenuOption[] => {
    return [
      {
        label: "Delete",
        className: styles.textWarning,
        onClick: () => removeFile(fileName),
      },
    ];
  };

  const handleFormSubmit = (_formData: unknown): void => {
    statusNotification(
      `${isSubtaskCreation ? "Subtask" : "Task"} created.`,
      "Success"
    );
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    if (!isDrawerOpen) {
      handleNavigateToTasksList();
    }
  }, [isDrawerOpen, handleNavigateToTasksList]);

  return (
    <TaskWorkflows>
      <Drawer
        isDrawerOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        footer={
          <Stack
            sx={{ width: "100%" }}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Upload
              multiple
              onChange={handlePersistFiles}
              renderChild={(onClick) => (
                <IconButton
                  iconClassName={styles.attachmentIcon}
                  onClick={onClick}
                  IconComponent={AttachFileIcon}
                />
              )}
            />

            <TaskActionButtons onCancel={handleCloseDrawer} />
          </Stack>
        }
      >
        <div className={styles.header}>
          <Text variant="title2">
            Create {isSubtaskCreation ? "Subtask" : "Task"}
          </Text>
        </div>

        {isLoading && (
          <div className={styles.loading}>
            <LogoLoadingStill />
          </div>
        )}
        {hasError && <div>Error loading template</div>}
        {template.success && (
          <FormEngine
            key="create-task-form"
            template={template.data}
            onSubmit={handleFormSubmit}
            initialValues={initialValues}
            externalResources={{ userRoles: ["cx"] }}
          />
        )}
        {!isLoading && !hasError && !template.success && (
          <div>No template available</div>
        )}

        <AttachmentsSection
          files={files}
          onCreateMenuOptions={handleCreateMenuOptions}
        />

        <TaskMessageModal
          title="Are you sure you want to leave without saving?"
          isOpen={isConfirmModalOpen}
          onClose={{
            label: "Stay",
            action: onConfirmModalClose,
          }}
          onConfirm={{
            label: "Leave",
            action: onConfirmModalConfirm,
          }}
        >
          <Text variant="body" color="textSubtle">
            Your changes will not be saved.
          </Text>
        </TaskMessageModal>
      </Drawer>
    </TaskWorkflows>
  );
};

const CreateTaskWorkflow: React.FC<CreateTaskWorkflowProps> = (props) => {
  return (
    <FormSubmitProvider>
      <CreateTaskWorkflowWithModal {...props} />
    </FormSubmitProvider>
  );
};

export default CreateTaskWorkflow;
